/**
 * QuoteValidityBadge — banner reutilizable que muestra "Cotización válida por X minutos"
 * con countdown en vivo, generando sensación de urgencia para que el cliente
 * complete el envío rápido (pedido por Carlos, jun 22, 2026).
 *
 * Cuando el countdown llega a 0:
 *  - Avisa visualmente que la cotización expiró.
 *  - Llama `onExpire` (opcional) para que el componente padre refresque la cotización.
 */
import React, { useEffect, useState } from 'react';

const VALIDITY_MS = 15 * 60 * 1000; // 15 minutos

export default function QuoteValidityBadge({
  quoteTimestamp,        // Date | string | number — momento en que se generó la cotización
  validityMs = VALIDITY_MS,
  onExpire,
  className = '',
}) {
  const [remainingMs, setRemainingMs] = useState(validityMs);
  const [expired, setExpired] = useState(false);

  useEffect(() => {
    if (!quoteTimestamp) return undefined;
    const startedAt = new Date(quoteTimestamp).getTime();
    if (Number.isNaN(startedAt)) return undefined;
    setExpired(false);
    const tick = () => {
      const left = startedAt + validityMs - Date.now();
      if (left <= 0) {
        setRemainingMs(0);
        if (!expired) {
          setExpired(true);
          if (typeof onExpire === 'function') onExpire();
        }
      } else {
        setRemainingMs(left);
      }
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [quoteTimestamp, validityMs]);

  if (!quoteTimestamp) return null;

  const totalSeconds = Math.max(0, Math.floor(remainingMs / 1000));
  const mm = Math.floor(totalSeconds / 60);
  const ss = totalSeconds % 60;
  const mmss = `${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}`;

  if (expired) {
    return (
      <div
        className={`flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-50 border border-amber-300 text-amber-800 text-xs font-semibold ${className}`}
        data-testid="quote-validity-expired"
      >
        <span aria-hidden>⌛</span>
        <span>Esta cotización venció. Vuelve a calcular para ver la tasa actual.</span>
      </div>
    );
  }

  const isUrgent = remainingMs < 60_000; // último minuto
  return (
    <div
      className={`flex items-center justify-between gap-2 px-3 py-2 rounded-lg border text-xs font-medium ${
        isUrgent
          ? 'bg-red-50 border-red-300 text-red-700'
          : 'bg-amber-50 border-amber-200 text-amber-800'
      } ${className}`}
      data-testid="quote-validity-badge"
    >
      <span className="flex items-center gap-1.5">
        <span aria-hidden>⏰</span>
        <span>Cotización válida por 15 minutos</span>
      </span>
      <span
        className={`tabular-nums font-bold ${isUrgent ? 'animate-pulse' : ''}`}
        data-testid="quote-validity-countdown"
      >
        {mmss}
      </span>
    </div>
  );
}
